import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { observer } from 'mobx-react';

import DesignSystemButton from '../../../Button/DesignSystemButton';
import { ActivityStepData } from '../Container';
import { COLORS } from '../../../../config/designSystem/Colors';
import { Caption1Bold, H4, ParagraphMRegular } from '../../../../config/designSystem/typography';
import { ChallengeCongratsFrame } from '../../../Challenges/ChallengeCongratsFrame';
import { ReminderFrame, ReminderSettings } from '../../../Challenges/ReminderFrame';
import { SPACING } from '../../../../config/designSystem/Spacing';
import { SessionPresentationProps } from '../../../Session/config';
import { TouchableText } from '../../../TouchableText/TouchableText';
import { convertFromUtf16 } from '../../../../utils/others/emoji';
import { registerBackInterceptor } from '../../../../utils/session/backInterceptor';
import { registerNextInterceptor } from '../../../../utils/session/nextInterceptor';
import { useBreakpointStyles } from '../../../../hooks/useBreakpointStyles';
import { useOnMount } from '../../../../hooks/useOnMount';

/**
 * figma: the in-session challenge step (Activities & Challenges pt1).
 * A staged flow that never leaves the branching session:
 *   proposal ("Let's make it part of your routine!")
 *     -> declined: "Maybe later" state, session continues
 *     -> accepted: reminders frame ("Let's turn your intention into a habit!")
 *         -> day congrats frame ("Great start, see you tomorrow!")
 *             -> session continues
 */
export interface PresentActivityStepProps extends SessionPresentationProps<ActivityStepData> {
  labels?: {
    TITLE?: string;
    MAKE_IT_ROUTINE?: string;
    FOR_A_WEEK?: string;
    FOR_X_DAYS?: string;
    ADD_TO_MY_PROGRAM?: string;
    MAYBE_LATER?: string;
    ADDED_TO_PROGRAM?: string;
    GREAT_START?: string;
    GREAT_JOB?: string;
    MAKE_IT_REAL?: string;
    REMINDER_HELPS?: string;
    ACTIVATE_REMINDERS?: string;
    SCHEDULE?: string;
    CALENDAR_HINT?: string;
    ADD_TO_GOOGLE_CALENDAR?: string;
    ADD_TO_CALENDAR?: string;
    CALENDAR_ADDED?: string;
    REMOVE_FROM_CHALLENGES?: string;
    SKIPPED_FOR_NOW?: string;
  }
  data: ActivityStepData,
}

/** "18:00" (stored) -> "6:00 PM" (picker) */
const to12h = (time?: string | null): string => {
  if (!time) {
    return '6:00 PM';
  }
  const [hoursRaw, minutes] = time.split(':');
  const hours = parseInt(hoursRaw, 10);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const displayed = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayed}:${minutes} ${suffix}`;
};

const DEFAULT_NUMBER_OF_DAYS = 7;

type Stage = 'proposal' | 'reminders' | 'congrats' | 'decided';

const _PresentActivityStep: FC<PresentActivityStepProps> = ({
  labels,
  data,
  onNext,
}) => {
  const activity = data.activity;
  const setDecided = data.setDecided;
  const acceptActivity = data.acceptActivity;
  const declineActivity = data.declineActivity;
  const saveReminder = data.saveReminder;
  const removeActivity = data.removeActivity;

  const alreadyRunning = !!activity?.userActivityId && activity?.status === 'started';
  const [stage, setStage] = useState<Stage>(alreadyRunning ? 'decided' : 'proposal');
  const [accepted, setAccepted] = useState<boolean>(activity?.status === 'started');
  const [interacted, setInteracted] = useState(false);

  useOnMount(() => {
    // a leftover decided=true from an earlier exercise/activity step in the
    // same session must not unlock Continue before the user chooses here
    setDecided(alreadyRunning);
  });

  // nothing to propose: unlock Continue so the session goes on
  useEffect(() => {
    if (data.missing) {
      setDecided(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.missing]);

  // the store can deliver the running challenge after first render (the
  // fetch races this mount): as long as the user hasn't chosen anything,
  // follow the store instead of proposing a challenge that already runs
  useEffect(() => {
    if (alreadyRunning && !interacted && stage === 'proposal') {
      setStage('decided');
      setAccepted(true);
      setDecided(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alreadyRunning, interacted, stage]);

  const styles = useBreakpointStyles({
    container: {
      flex: 1,
      height: '100%',
    },
    wrapper: {
      flex: 1,
      lg: {
        justifyContent: 'center',
      },
    },
    contentWrapper: {
      flex: 1,
      lg: {
        marginHorizontal: SPACING['sp-20'],
        marginBottom: SPACING['sp-12'],
      },
    },
    scrollContainer: {
      marginBottom: SPACING['sp-20'],
      flex: 1,
      lg: {
        marginBottom: 0,
      },
    },
    scrollContent: {
      marginHorizontal: SPACING['sp-20'],
      paddingTop: SPACING['sp-32'],
      lg: {
        marginHorizontal: 0,
      },
    },
    divider: {
      alignSelf: 'center',
      width: SPACING['sp-48'],
      height: 3,
      borderRadius: 2,
      backgroundColor: COLORS.content.accent,
      marginVertical: SPACING['sp-24'],
    },
    durationRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      marginBottom: SPACING['sp-24'],
    },
    durationPill: {
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: SPACING['sp-8'],
      backgroundColor: COLORS.surface.accent,
      borderRadius: 999,
      paddingVertical: SPACING['sp-8'],
      paddingHorizontal: SPACING['sp-16'],
    },
    description: {
      marginHorizontal: SPACING['sp-12'],
    },
    actions: {
      alignItems: 'center',
      alignSelf: 'center',
      // same width as the session Continue button below it
      width: '90%',
      maxWidth: 600,
    },
    maybeLater: {
      marginTop: SPACING['sp-16'],
      alignItems: 'center',
    },
    stateAcceptance: {
      justifyContent: 'center',
      alignItems: 'center',
      padding: SPACING['sp-16'],
    },
  });

  // the reminder settings as edited on the frame; saved when the session goes on
  const reminderRef = useRef<ReminderSettings | null>(null);
  const stageRef = useRef<Stage>(stage);
  stageRef.current = stage;

  const onAcceptedActivity = useCallback(async () => {
    setInteracted(true);
    setAccepted(true);
    if (accepted && activity?.userActivityId) {
      // came back from the reminders: the challenge already runs
      setStage('reminders');
      setDecided(true);
      return;
    }
    const created = await acceptActivity();
    if (created) {
      setStage('reminders');
      setDecided(true);
    } else {
      // creation genuinely failed: let the user retry or decline — but never
      // leave the step undecidable
      setAccepted(false);
      setStage('proposal');
      setInteracted(false);
    }
  }, [acceptActivity, accepted, activity?.userActivityId, setDecided]);

  // Continue on the reminders saves them and shows the congratulations (one
  // press consumed); on the revisited reminders it saves and moves on
  useEffect(() => registerNextInterceptor(() => {
    if (stageRef.current === 'reminders') {
      if (reminderRef.current) {
        saveReminder(reminderRef.current);
      }
      setStage('congrats');
      return true;
    }
    if (stageRef.current === 'decided' && reminderRef.current) {
      saveReminder(reminderRef.current);
    }
    return false;
  }), [saveReminder]);

  // Back inside the step: reminders -> proposal, congratulations -> reminders
  useEffect(() => registerBackInterceptor(() => {
    if (stageRef.current === 'reminders') {
      // the proposal is a choice again: no Continue until it is made
      setStage('proposal');
      setDecided(false);
      return true;
    }
    if (stageRef.current === 'congrats') {
      setStage('reminders');
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const onDeclinedActivity = useCallback(() => {
    setInteracted(true);
    setAccepted(false);
    setStage('decided');
    setDecided(true);
    declineActivity?.();
    // nothing to show after a refusal: the session goes straight on (the
    // step used to stay on a blank frame reading "Maybe later")
    onNext?.();
  }, [setDecided, declineActivity, onNext]);

  const onReminderChange = useCallback((settings: ReminderSettings) => {
    reminderRef.current = settings;
  }, []);
  const calendar = { title: activity?.title || '', days: activity?.numberOfDays || DEFAULT_NUMBER_OF_DAYS };

  // leaving the challenge from the step: back to the proposal, and the
  // session may go on either way
  const onRemove = useCallback(async () => {
    setInteracted(true);
    const removed = await removeActivity();
    if (removed) {
      setAccepted(false);
      setStage('proposal');
      setDecided(true);
    }
  }, [removeActivity, setDecided]);

  const renderDuration = () => {
    const numberOfDays = activity?.numberOfDays || DEFAULT_NUMBER_OF_DAYS;
    const durationLabel = numberOfDays === DEFAULT_NUMBER_OF_DAYS
      ? (labels?.FOR_A_WEEK || 'For a week')
      : (labels?.FOR_X_DAYS || 'For {{numberOfDays}} days').replace('{{numberOfDays}}', String(numberOfDays));
    return (
      <View style={styles.durationRow}>
        <View style={styles.durationPill}>
          <Caption1Bold color={COLORS.content.accent}>
            {convertFromUtf16('1F4C5')}
          </Caption1Bold>
          <Caption1Bold color={COLORS.content.accent}>
            {durationLabel}
          </Caption1Bold>
        </View>
      </View>
    );
  };

  const renderProposal = () => (
    <View style={styles.wrapper}>
      <View style={styles.contentWrapper}>
        <ScrollView
          style={styles.scrollContainer}
          contentContainerStyle={styles.scrollContent}
        >
          <View>
            <H4 align={true}>{labels?.MAKE_IT_ROUTINE || 'Let\'s make it part of your routine!'}</H4>
            <View style={styles.divider} />
            {renderDuration()}
            <View style={styles.description}>
              <ParagraphMRegular selectable={true} align={true} color={COLORS.content.secondary}>
                {activity?.shortDescription}
              </ParagraphMRegular>
            </View>
          </View>
        </ScrollView>
      </View>
      <View style={styles.actions}>
        <DesignSystemButton
          label={labels?.ADD_TO_MY_PROGRAM || 'Add to my program'}
          onPress={onAcceptedActivity}
        />
        <View style={styles.maybeLater}>
          <TouchableText
            onPress={onDeclinedActivity}
            label={labels?.MAYBE_LATER || 'Maybe later'}
          />
        </View>
      </View>
    </View>
  );

  const renderDecidedState = () => {
    // running challenge, revisited (back and forth in the session, or the
    // challenge was already running): the reminder settings stay reachable,
    // and the challenge can be dropped from here
    if (accepted) {
      return (
        <ReminderFrame
          designSystemColor={activity?.designSystemColor}
          defaultEnabled={activity?.reminderEnabled !== false}
          defaultTime={to12h(activity?.reminderTime)}
          onChange={onReminderChange}
          calendar={calendar}
          onRemove={onRemove}
          removeLabel={labels?.REMOVE_FROM_CHALLENGES || 'Remove from my daily challenges'}
          labels={labels}
        />
      );
    }
    // declined, and still on the step (the session could not move on):
    // say so, and keep the door open
    return (
      <View style={[styles.wrapper, { justifyContent: 'center' }]}>
        <View style={styles.stateAcceptance}>
          <ParagraphMRegular selectable={true} align={true} color={COLORS.content.secondary}>
            {labels?.SKIPPED_FOR_NOW || 'Skipped for now. You can still make it part of your routine.'}
          </ParagraphMRegular>
        </View>
        <View style={styles.actions}>
          <DesignSystemButton
            label={labels?.ADD_TO_MY_PROGRAM || 'Add to my program'}
            onPress={onAcceptedActivity}
          />
        </View>
      </View>
    );
  };

  const renderCongrats = () => {
    const days = activity?.days?.slice() || [];
    const doneCount = days.filter((day: { done: boolean }) => day.done).length;
    const headline = doneCount <= 1
      ? (labels?.GREAT_START || 'Great start, see you tomorrow! 👋')
      : (labels?.GREAT_JOB || 'Another day done, well done! 👏');
    return (
      <ChallengeCongratsFrame
        headline={headline}
        days={days}
        designSystemColor={activity?.designSystemColor}
      />
    );
  };

  if (data.missing) {
    return <View style={styles.container} />;
  }

  return (
    <View style={styles.container}>
      {stage === 'proposal' && renderProposal()}
      {stage === 'reminders' && (
        <ReminderFrame
          designSystemColor={activity?.designSystemColor}
          onChange={onReminderChange}
          calendar={calendar}
          labels={labels}
        />
      )}
      {stage === 'congrats' && renderCongrats()}
      {stage === 'decided' && renderDecidedState()}
    </View>
  );
};

export const PresentActivityStep = observer(_PresentActivityStep);
